import { List, Map, Set } from 'immutable';

import { getBarcodeError, getCAPError, getCPIError, getGTINError, getPriceError, getQuantityError, getRetailPriceError, getSKUError, getWholesalePriceError } from './inventory';
import { getHeightError, getLengthError, getProcessingProfileError, getWeightError, getWidthError } from './shipping';
import { toLowerCase } from '../string';
import { setInvalid } from '../variations/errors';
import { getSize } from '../iterable/getSize';
import { reduce } from '../iterable/reduce';
import { get } from '../iterable/get';

import { ALL, ATTRIBUTES_TO_VALIDATE, CHANNEL_VARIANTS, COMBINATIONS, DELETED, ERRORS, INFLUENCE, NUMBER_OF_VARIANTS, RETAIN, VALIDATIONS, VARIANTS } from '../../constants/variations';
import { CURRENCIES, CURRENCY, ERROR, REQUIRED, TAXONOMY_PROPERTY, VALUE } from '../../constants/product';
import { EBAY, ETSY, FAIRE, SHOPIFY } from '../../constants/channels';
import { ATTRIBUTE } from '../../constants/attributes';
import { EMPTY } from '../../constants';

export function getCombinationError({ attribute, channel, shopData, variations }) {
  const combinations = variations.get(COMBINATIONS);

  function reduceCombinationsToQuantity(result, combination) {
    return result && (!combination.get(ATTRIBUTE.VISIBILITY) || !parseInt(combination.get(ATTRIBUTE.QUANTITY)));
  }

  function reduceCombinationsToVisibility(result, combination) {
    return result && !combination.get(ATTRIBUTE.VISIBILITY);
  }

  function shouldValidate(combination) {
    switch (channel) {
      case EBAY: {
        return true;
      }

      case ETSY: {
        return combination.get(ATTRIBUTE.VISIBILITY) && getSize(variations.getIn([INFLUENCE, attribute])) > 0;
      }

      case FAIRE: {
        switch (attribute) {
          case ATTRIBUTE.GTIN:
          case ATTRIBUTE.HEIGHT:
          case ATTRIBUTE.LENGTH:
          case ATTRIBUTE.WEIGHT:
          case ATTRIBUTE.WIDTH: {
            return true;
          }

          default: {
            return false;
          }
        }
      }

      case SHOPIFY: {
        if (combination.get(DELETED)) return false;

        switch (attribute) {
          case ATTRIBUTE.QUANTITY: {
            return combination.get(ATTRIBUTE.TRACK_QUANTITY, false);
          }

          case ATTRIBUTE.WEIGHT: {
            return combination.get(ATTRIBUTE.PHYSICAL, false);
          }

          default: {
            return true;
          }
        }
      }

      default: {
        return false;
      }
    }
  }

  function mapCombinationsForBarcodeError(combination) {
    return shouldValidate(combination) && getBarcodeError({ channel, value: combination.get(ATTRIBUTE.BARCODE) });
  }

  function mapCombinationsForCAPError(combination) {
    return shouldValidate(combination) && getCAPError({ channel, value: combination.get(ATTRIBUTE.CAP) });
  }

  function mapCombinationsForCPIError(combination) {
    return shouldValidate(combination) && getCPIError({ channel, value: combination.get(ATTRIBUTE.CPI) });
  }

  function mapCombinationsForGTINError(combination) {
    return shouldValidate(combination) && getGTINError({ channel, value: combination.get(ATTRIBUTE.GTIN) });
  }

  function mapCombinationsForHeightError(combination) {
    return shouldValidate(combination) && getHeightError({ channel, value: combination.get(ATTRIBUTE.HEIGHT) });
  }

  function mapCombinationsForLengthError(combination) {
    return shouldValidate(combination) && getLengthError({ channel, value: combination.get(ATTRIBUTE.LENGTH) });
  }

  function mapCombinationsForSKUError(combination) {
    return (
      shouldValidate(combination) &&
      getSKUError({ allowEmpty: variations.has(RETAIN), channel, value: combination.get(ATTRIBUTE.SKU) })
    );
  }

  function mapCombinationsForPriceError(combination) {
    return (
      shouldValidate(combination) &&
      getPriceError({ allowEmpty: variations.has(RETAIN), channel, value: combination.get(ATTRIBUTE.PRICE) })
    );
  }

  function mapCombinationsForProcessingProfileError(combination) {
    return (
      shouldValidate(combination) &&
      getProcessingProfileError({
        allowEmpty: variations.has(RETAIN),
        shopData,
        value: combination.get(ATTRIBUTE.PROCESSING_PROFILE),
      })
    );
  }

  function mapCombinationsForQuantityError(combination) {
    return (
      shouldValidate(combination) &&
      getQuantityError({
        allowEmpty: variations.has(RETAIN),
        allowZero: true,
        channel,
        value: combination.get(ATTRIBUTE.QUANTITY),
      })
    );
  }

  function mapCombinationsForRetailPriceError(combination) {
    function reduceCurrenciesForRetailPriceError(errors, currency) {
      const error = currency === CURRENCY.USD || variations.getIn([INFLUENCE, ATTRIBUTE.INTERNATIONAL_PRICING])
        ? getRetailPriceError({
          allowEmpty: variations.has(RETAIN),
          allowZero: !variations.getIn([REQUIRED, ATTRIBUTE.PRICE]),
          currency,
          retail: combination.getIn([ATTRIBUTE.RETAIL_PRICE, currency]),
          wholesale: combination.getIn([ATTRIBUTE.WHOLESALE_PRICE, currency]),
        })
        : false;

      return errors.set(currency, error);
    }

    return CURRENCIES[FAIRE].reduce(reduceCurrenciesForRetailPriceError, EMPTY.MAP);
  }

  function mapCombinationsForWeightError(combination) {
    return shouldValidate(combination) && getWeightError({ channel, value: combination.get(ATTRIBUTE.WEIGHT) });
  }

  function mapCombinationsForWholesalePriceError(combination) {
    function reduceCurrenciesForWholesalePriceError(errors, currency) {
      const error = currency === CURRENCY.USD || variations.getIn([INFLUENCE, ATTRIBUTE.INTERNATIONAL_PRICING])
        ? getWholesalePriceError({
          allowEmpty: variations.has(RETAIN),
          allowZero: !variations.getIn([REQUIRED, ATTRIBUTE.PRICE]),
          currency,
          retail: combination.getIn([ATTRIBUTE.RETAIL_PRICE, currency]),
          wholesale: combination.getIn([ATTRIBUTE.WHOLESALE_PRICE, currency]),
        })
        : false;

      return errors.set(currency, error);
    }

    return CURRENCIES[FAIRE].reduce(reduceCurrenciesForWholesalePriceError, EMPTY.MAP);
  }

  function mapCombinationsForWidthError(combination) {
    return shouldValidate(combination) && getWidthError({ channel, value: combination.get(ATTRIBUTE.WIDTH) });
  }

  function reduceCombinationsForSKUError(result, combination, combinationIndex) {
    const sku = combination.get(ATTRIBUTE.SKU);
    const allowEmpty = !variations.getIn([REQUIRED, ATTRIBUTE.SKU]);
    const error = getSKUError({ allowEmpty, channel, value: sku });
    result.errors = result.errors.set(combinationIndex, error);

    if (sku) {
      if (result.sku.has(sku)) {
        const duplicateIndex = result.sku.get(sku);

        if (!result.errors.get(duplicateIndex)) {
          result.errors = result.errors.set(duplicateIndex, ERRORS.COMBINATIONS.SKU_NON_UNIQUE);
        }

        if (!result.errors.get(combinationIndex)) {
          result.errors = result.errors.set(combinationIndex, ERRORS.COMBINATIONS.SKU_NON_UNIQUE);
        }
      } else {
        result.sku = result.sku.set(sku, combinationIndex);
      }
    }

    return result;
  }

  switch (attribute) {
    case ATTRIBUTE.BARCODE: {
      if (variations.getIn([RETAIN, ATTRIBUTE.BARCODE])) {
        return List(Array(getSize(combinations)).fill(false));
      }

      return combinations.map(mapCombinationsForBarcodeError);
    }

    case ATTRIBUTE.CAP: {
      if (variations.getIn([RETAIN, ATTRIBUTE.PRICE])) {
        return List(Array(getSize(combinations)).fill(false));
      }

      return combinations.map(mapCombinationsForCAPError);
    }

    case ATTRIBUTE.CPI: {
      if (variations.getIn([RETAIN, ATTRIBUTE.PRICE])) {
        return List(Array(getSize(combinations)).fill(false));
      }

      return combinations.map(mapCombinationsForCPIError);
    }

    case ATTRIBUTE.GTIN: {
      if (variations.getIn([RETAIN, ATTRIBUTE.GTIN])) {
        return List(Array(getSize(combinations)).fill(false));
      }

      return combinations.map(mapCombinationsForGTINError);
    }

    case ATTRIBUTE.HEIGHT: {
      if (variations.getIn([RETAIN, ATTRIBUTE.SHIPPING])) {
        return List(Array(getSize(combinations)).fill(false));
      }

      return combinations.map(mapCombinationsForHeightError);
    }

    case ATTRIBUTE.LENGTH: {
      if (variations.getIn([RETAIN, ATTRIBUTE.SHIPPING])) {
        return List(Array(getSize(combinations)).fill(false));
      }

      return combinations.map(mapCombinationsForLengthError);
    }

    case ATTRIBUTE.PRICE: {
      if (variations.getIn([RETAIN, ATTRIBUTE.PRICE])) {
        return List(Array(getSize(combinations)).fill(false));
      }

      return combinations.map(mapCombinationsForPriceError);
    }

    case ATTRIBUTE.PROCESSING_PROFILE: {
      if (variations.getIn([RETAIN, ATTRIBUTE.PROCESSING_PROFILE])) {
        return List(Array(getSize(combinations)).fill(false));
      }

      return combinations.map(mapCombinationsForProcessingProfileError);

    }

    case ATTRIBUTE.RETAIL_PRICE: {
      function reduceCurrenciesToEmptyErrors(errors, currency) {
        return errors.set(currency, false);
      }

      function mapCombinationsEmptyRetailPriceErrors() {
        return CURRENCIES[channel].reduce(reduceCurrenciesToEmptyErrors, EMPTY.MAP);
      }

      if (variations.getIn([RETAIN, ATTRIBUTE.PRICE])) {
        return combinations.map(mapCombinationsEmptyRetailPriceErrors);
      }

      return combinations.map(mapCombinationsForRetailPriceError);
    }

    case ATTRIBUTE.QUANTITY: {
      if (variations.getIn([RETAIN, ATTRIBUTE.QUANTITY])) {
        return List(Array(getSize(combinations)).fill(false));
      }

      switch (channel) {
        case ETSY: {
          if (combinations.reduce(reduceCombinationsToQuantity, true)) {
            return getSize(variations.getIn([INFLUENCE, attribute])) > 0 && ERRORS.COMBINATIONS.NO_STOCK;
          }

          return combinations.map(mapCombinationsForQuantityError);
        }

        default: {
          return combinations.map(mapCombinationsForQuantityError);
        }
      }
    }

    case ATTRIBUTE.SKU: {
      if (variations.getIn([RETAIN, ATTRIBUTE.SKU])) {
        return List(Array(getSize(combinations)).fill(false));
      }

      switch (channel) {
        case EBAY: {
          const { errors } = combinations.reduce(reduceCombinationsForSKUError, { errors: EMPTY.LIST, sku: EMPTY.MAP });
          return errors;
        }

        default: {
          return combinations.map(mapCombinationsForSKUError);
        }
      }
    }

    case ATTRIBUTE.VISIBILITY: {
      return (
        getSize(combinations) > 0 &&
        combinations.reduce(reduceCombinationsToVisibility, true) &&
        ERRORS.COMBINATIONS.NO_VISIBLE
      );
    }

    case ATTRIBUTE.WEIGHT: {
      if (variations.getIn([RETAIN, ATTRIBUTE.SHIPPING])) {
        return List(Array(getSize(combinations)).fill(false));
      }

      return combinations.map(mapCombinationsForWeightError);
    }

    case ATTRIBUTE.WHOLESALE_PRICE: {
      function reduceCurrenciesToEmptyErrors(errors, currency) {
        return errors.set(currency, false);
      }

      function mapCombinationsEmptyRetailPriceErrors() {
        return CURRENCIES[channel].reduce(reduceCurrenciesToEmptyErrors, EMPTY.MAP);
      }

      if (variations.getIn([RETAIN, ATTRIBUTE.PRICE])) {
        return combinations.map(mapCombinationsEmptyRetailPriceErrors);
      }

      return combinations.map(mapCombinationsForWholesalePriceError);
    }

    case ATTRIBUTE.WIDTH: {
      if (variations.getIn([RETAIN, ATTRIBUTE.SHIPPING])) {
        return List(Array(getSize(combinations)).fill(false));
      }

      return combinations.map(mapCombinationsForWidthError);
    }

    default: {
      return undefined;
    }
  }
}

export function getCombinationsErrors({ channel, shopData, variations }) {
  function reduceAttributes(errors, attribute) {
    return errors.set(attribute, getCombinationError({ attribute, channel, shopData, variations }));
  }

  return ATTRIBUTES_TO_VALIDATE[channel].reduce(reduceAttributes, variations.get(ERROR, EMPTY.MAP));
}

export function getOptionNameError({ channel, value }) {
  if (
    VALIDATIONS.OPTIONS.MAXIMUM_NAME_LENGTH.hasOwnProperty(channel) &&
    getSize(value) > VALIDATIONS.OPTIONS.MAXIMUM_NAME_LENGTH[channel]
  ) {
    return ERRORS.OPTIONS.NAME_LENGTH_EXCEEDED[channel];
  }

  switch (channel) {
    case ETSY: {
      return /[\^$`]/.test(value) && ERRORS.OPTIONS.FORBIDDEN_CHARACTERS[channel];
    }

    default: {
      return false;
    }
  }
}

export function getOptionsErrors({ channel, variations }) {
  const errors = variations.get(ERROR, EMPTY.MAP);

  function setOptionErrors(result, variant, variantIndex) {
    function mapOptions(option) {
      const value = option.get('value');

      function reduceAffectingOptions(affecting, item) {
        return affecting || variations.hasIn([INFLUENCE, variantIndex, 'values', item.get('value'), value]);
      }

      switch (channel) {
        case EBAY: {
          if (variations.hasIn([INFLUENCE, variantIndex, 'affecting'])) {
            switch (variations.getIn([INFLUENCE, variantIndex, 'type'])) {
              case TAXONOMY_PROPERTY.BOOL:
              case TAXONOMY_PROPERTY.MULTI:
              case TAXONOMY_PROPERTY.SINGLE: {
                if (!variations.hasIn([INFLUENCE, variantIndex, 'values', value])) {
                  return ERRORS.OPTIONS.NOT_ALLOWED;
                }

                return false;
              }

              default: {
                break;
              }
            }
          } else if (variations.hasIn([INFLUENCE, variantIndex, 'affectedBy'])) {
            switch (variations.getIn([INFLUENCE, variantIndex, 'type'])) {
              case TAXONOMY_PROPERTY.BOOL:
              case TAXONOMY_PROPERTY.MULTI:
              case TAXONOMY_PROPERTY.SINGLE: {
                const affectedBy = variations.getIn([INFLUENCE, variantIndex, 'affectedBy']);

                switch (variations.getIn([INFLUENCE, affectedBy, 'type'])) {
                  case TAXONOMY_PROPERTY.BOOL:
                  case TAXONOMY_PROPERTY.MULTI:
                  case TAXONOMY_PROPERTY.SINGLE: {
                    if (!variations.getIn([VARIANTS, affectedBy, 'options']).reduce(reduceAffectingOptions, false)) {
                      return ERRORS.OPTIONS.NOT_ALLOWED;
                    }

                    return false;
                  }

                  default: {
                    break;
                  }
                }

                break;
              }

              default: {
                break;
              }
            }

          }

          return getOptionNameError({ channel, value });
        }

        default: {
          return getOptionNameError({ channel, value });
        }
      }

    }

    return result.set(variantIndex, variant.get('options').map(mapOptions));
  }

  return errors.set('options', variations.get(VARIANTS).reduce(setOptionErrors, errors.get('options', EMPTY.LIST)));
}

export function getForbiddenVariationNames({ category, channel, itemSpecifics = EMPTY.MAP, taxonomyData }) {
  if (channel !== EBAY) return undefined;

  function reduceCustom(result, key) {
    return result.add(toLowerCase(itemSpecifics.getIn([VALUE, key, 'name'])));
  }

  function reduceProperties(result, property, name) {
    return property.variations && getSize(itemSpecifics.getIn([VALUE, name, 'value'])) === 0
      ? result
      : result.add(toLowerCase(name));
  }

  function reduceItemSpecifics(result, attribute) {
    return getSize(attribute.get('value')) > 0
      ? result.add(toLowerCase(attribute.get('name')))
      : result;
  }

  const properties = get(['CATEGORIES', category, 'properties'])(taxonomyData) || {};
  const initial = Set([toLowerCase(ATTRIBUTE.QUANTITY)]);

  return getSize(properties) > 0
    ? reduce(properties, itemSpecifics.get('custom', EMPTY.MAP).reduce(reduceCustom, initial), reduceProperties)
    : itemSpecifics.get(VALUE, EMPTY.MAP).reduce(reduceItemSpecifics, initial);
}

export function getVariantNameError({ channel, forbidden = EMPTY.SET, name, variants = EMPTY.LIST, variantIndex = 0 }) {
  if (
    VALIDATIONS.VARIANTS.MAXIMUM_NAME_LENGTH.hasOwnProperty(channel) &&
    getSize(name) > VALIDATIONS.VARIANTS.MAXIMUM_NAME_LENGTH[channel]
  ) {
    return ERRORS.VARIANTS.NAME_LENGTH_EXCEEDED[channel];
  }

  function findSameVariant(variant) {
    return variant.get('name') === name && getSize(variant.get('options')) > 0;
  }

  function findSameVariantCaseInsensitive(variant) {
    return toLowerCase(variant.get('name')) === toLowerCase(name) && getSize(variant.get('options')) > 0;
  }

  switch (channel) {
    case EBAY: {
      if (
        !name &&
        variants.getIn([variantIndex, 'name']) !== undefined &&
        getSize(variants.getIn([variantIndex, 'options'])) > 0
      ) {
        return ERRORS.VARIANTS.NAME_REQUIRED;
      }

      if (forbidden.has(toLowerCase(name))) {
        return ERRORS.VARIANTS.NAME_FORBIDDEN;
      }

      return (
        variants.delete(variantIndex).findIndex(findSameVariantCaseInsensitive) !== -1 &&
        ERRORS.VARIANTS.NAME_EXIST
      );
    }

    case ETSY: {
      return /[\^$`]/.test(name) && ERRORS.VARIANTS.FORBIDDEN_CHARACTERS[ETSY];
    }

    case FAIRE: {
      if (
        !name &&
        variants.getIn([variantIndex, 'name']) !== undefined &&
        getSize(variants.getIn([variantIndex, 'options'])) > 0
      ) {
        return ERRORS.VARIANTS.NAME_REQUIRED;
      }

      return (
        variants.delete(variantIndex).findIndex(findSameVariant) !== -1 &&
        ERRORS.VARIANTS.NAME_EXIST
      );
    }

    case SHOPIFY: {
      if (
        !name &&
        variants.getIn([variantIndex, 'name']) !== undefined &&
        getSize(variants.getIn([variantIndex, 'options'])) > 0
      ) {
        return ERRORS.VARIANTS.NAME_REQUIRED;
      } else if (/\s\/\s/.test(name)) {
        return ERRORS.VARIANTS.FORBIDDEN_CHARACTERS[SHOPIFY];
      }

      return (
        variants.delete(variantIndex).findIndex(findSameVariant) !== -1 &&
        ERRORS.VARIANTS.NAME_EXIST
      );
    }

    default: {
      return false;
    }
  }
}

export function getVariantsErrors({ channel, variations }) {
  function filterDeleted(combination) {
    return !combination.get(DELETED);
  }

  function setVariantsLimitError(errors) {
    if (getSize(variations.get(VARIANTS)) > NUMBER_OF_VARIANTS[channel]) {
      return errors.set('variantsNumber', ERRORS.VARIANTS.NUMBER_EXCEEDED[channel]);
    }

    return errors.delete('variantsNumber');
  }

  function reduceVariants(errors, variant, variantIndex) {
    switch (channel) {
      case EBAY: {
        if (getSize(variant.get('options')) === 0) {
          return errors.setIn([variantIndex, 'options'], ERRORS.OPTIONS.NO_OPTIONS);
        }

        if (getSize(variations.get(COMBINATIONS)) > VALIDATIONS.COMBINATIONS.MAXIMUM_NUMBER[EBAY]) {
          return errors.setIn([variantIndex, 'options'], ERRORS.COMBINATIONS.NUMBER_EXCEEDED[EBAY]);
        }

        if (getSize(variant.get('options')) > VALIDATIONS.OPTIONS.MAXIMUM_NUMBER[EBAY]) {
          return errors.setIn([variantIndex, 'options'], ERRORS.OPTIONS.NUMBER_EXCEEDED[EBAY]);
        }

        return errors.deleteIn([variantIndex, 'options']);
      }

      case ETSY: {
        if (getSize(variant.get('options')) === 0) {
          return errors.setIn([variantIndex, 'options'], ERRORS.OPTIONS.NO_OPTIONS);
        }

        if (variations.getIn([INFLUENCE, ALL])) {
          if (getSize(variations.get(COMBINATIONS)) > VALIDATIONS.COMBINATIONS.MAXIMUM_NUMBER[ETSY]) {
            return errors.setIn([variantIndex, 'options'], ERRORS.COMBINATIONS.NUMBER_EXCEEDED[ETSY]);
          }
        } else if (getSize(variant.get('options')) > VALIDATIONS.OPTIONS.MAXIMUM_NUMBER[ETSY]) {
          return errors.setIn([variantIndex, 'options'], ERRORS.OPTIONS.NUMBER_EXCEEDED[ETSY]);
        }

        return errors.deleteIn([variantIndex, 'options']);
      }

      case FAIRE: {
        if (getSize(variant.get('options')) === 0) {
          return errors.setIn([variantIndex, 'options'], ERRORS.OPTIONS.NO_OPTIONS);
        }

        return errors.deleteIn([variantIndex, 'options']);
      }

      case SHOPIFY: {
        if (getSize(variant.get('options')) === 0) {
          return errors.setIn([variantIndex, 'options'], ERRORS.OPTIONS.NO_OPTIONS);
        }

        if (
          getSize(variations.get(COMBINATIONS).filter(filterDeleted)) >
          VALIDATIONS.COMBINATIONS.MAXIMUM_NUMBER[SHOPIFY]
        ) {
          return errors.setIn([variantIndex, 'options'], ERRORS.COMBINATIONS.NUMBER_EXCEEDED[SHOPIFY]);
        }

        if (getSize(variant.get('options')) > VALIDATIONS.OPTIONS.MAXIMUM_NUMBER[SHOPIFY]) {
          return errors.setIn([variantIndex, 'options'], ERRORS.OPTIONS.NUMBER_EXCEEDED[SHOPIFY]);
        }

        return errors.deleteIn([variantIndex, 'options']);
      }

      default: {
        return errors;
      }
    }
  }

  return setVariantsLimitError(
    variations
      .get(ERROR, EMPTY.MAP)
      .set(VARIANTS, variations.get(VARIANTS).reduce(reduceVariants, variations.getIn([ERROR, VARIANTS], EMPTY.MAP)))
  );
}

export function updateVariationsErrors({ category, channel, itemSpecifics, shopData, taxonomyData, variations }) {
  const variants = variations.get(VARIANTS);

  function reduceVariants(errors, variantIndex) {
    if (!variants.has(variantIndex)) {
      return errors.deleteIn([VARIANTS, variantIndex]);
    }

    const forbidden = getForbiddenVariationNames({ category, channel, itemSpecifics, taxonomyData });
    const name = variants.getIn([variantIndex, 'name']);
    const error = getVariantNameError({ channel, forbidden, name, variants, variantIndex });
    return errors.setIn([VARIANTS, variantIndex], Map({ name: error }));
  }

  function updateErrors(errors = EMPTY.MAP) {
    return getCombinationsErrors({
      channel,
      shopData,
      variations: variations.set(ERROR, getVariantsErrors({
        channel,
        variations: variations.set(ERROR, getOptionsErrors({
          channel,
          variations: variations.set(ERROR,
            CHANNEL_VARIANTS[channel].reduce(reduceVariants, errors)
          ),
        })),
      })),
    });
  }

  return setInvalid(variations.update(ERROR, updateErrors));
}
